import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { H3, H4, Span } from '@/components/atoms'
import {
  DatePickerField,
  SelectField,
  FileUpload,
} from '@/components/molecules'
import {
  WEIGHT_OPTIONS,
  type PetFormValues,
} from '../page.onboarding.pet-form.hook'
import type { Pet } from '@/shared/types/global.types'

interface PetFormStepOnePageProps {
  pet?: Pet
}

export function PetFormStepOnePage({ pet }: PetFormStepOnePageProps) {
  const { t } = useTranslation()
  const { control } = useFormContext<PetFormValues>()

  const weightOptions = WEIGHT_OPTIONS.map((o) => ({
    value: o.value,
    label: t(`onboarding.pet.form.stepOne.weightOptions.${o.key}`),
  }))

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4 md:hidden">
        <H3 className="text-[2rem]">
          {`${t('onboarding.pet.form.titleStart')} `}
          <span className="text-indigo-base capitalize">{`${pet?.name}!`}</span>
        </H3>
        <Span className="text-sm leading-6 text-neutral-900">
          {t('onboarding.pet.form.subtitle')}
        </Span>
        <H4>{t('onboarding.pet.form.stepOne.stepLabel')}</H4>
      </div>
      <H3 className="hidden md:block">
        {t('onboarding.pet.form.stepOne.stepLabel')}
      </H3>

      <div className="flex w-full flex-col gap-6 md:w-72.5">
        <Controller
          control={control}
          name="birthDate"
          render={({ field }) => (
            <DatePickerField
              label={t('onboarding.pet.form.stepOne.birthdayLabel')}
              value={field.value}
              placeholder="00/00/0000"
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDate={new Date()}
              panelPosition="relative"
              dropdownMonthYear
            />
          )}
        />
        <Controller
          control={control}
          name="weight"
          render={({ field }) => (
            <SelectField
              label={t('onboarding.pet.form.stepOne.weightLabel')}
              value={field.value}
              options={weightOptions}
              placeholder={t('onboarding.pet.form.stepOne.weightPlaceholder')}
              onSelect={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name="petPictureBase64"
        render={({ field }) => (
          <FileUpload
            label={t('onboarding.pet.form.stepOne.photoLabel', {
              name: pet?.name
                ? pet.name.charAt(0).toUpperCase() + pet.name.slice(1)
                : '',
            })}
            variant="image"
            previewUrl={field.value ?? pet?.petPictureUrl}
            onFileSelect={(file) => {
              const reader = new FileReader()
              reader.onload = (e) => field.onChange(e.target?.result as string)
              reader.readAsDataURL(file)
            }}
          />
        )}
      />
    </div>
  )
}
